import type { FileDownloadResult } from '../services/fileDownload';
import type {
    CreateContractInvitationPayload,
    CreateQuotePayload,
    CargoInsuranceChoice,
    CreateShipmentRequestPayload,
    UpdateShipmentRequestSchedulePayload,
    MarketplaceApiError,
    MarketplacePaginationMeta,
    OperationMode,
    OperatorBankAccount,
    OperatorProfile,
    OperatorReturnIntent,
    ReturnIntentStatus,
    OperatorTrailer,
    OperatorVehicle,
    SaveHomeBasePayload,
    TrailerPhoto,
    VehiclePhoto,
    ShipmentContract,
    ShipmentContractFile,
    ShipmentContractFileCategory,
    ShipmentContractFileScope,
    ShipmentContractListStatusFilter,
    CreateContractAssignmentPayload,
    ShipmentQuote,
    ShipmentRequest,
    ShipmentRequestListStatusFilter,
    UpdateContractPointsPayload,
    UpsertOperatorBankAccountPayload,
    UpsertOperatorProfilePayload,
    ActivateReturnIntentPayload,
    UpsertOperatorTrailerPayload,
    UpsertOperatorVehiclePayload,
} from '../types/marketplace';

export interface MarketplaceState {
    operationMode: OperationMode | null;
    modeLoaded: boolean;
    isLoadingMode: boolean;

    operatorProfile: OperatorProfile | null;
    operatorBankAccounts: OperatorBankAccount[];
    operatorVehicles: OperatorVehicle[];
    operatorTrailers: OperatorTrailer[];
    canOperatorQuote: boolean;
    operatorQuoteReason: string | null;
    operatorProfileLoaded: boolean;
    isLoadingOperator: boolean;
    isSavingOperator: boolean;
    isLoadingTrailers: boolean;
    activeReturnIntent: OperatorReturnIntent | null;
    returnIntentLoaded: boolean;
    isLoadingReturnIntent: boolean;
    isSavingReturnIntent: boolean;
    returnStatus: ReturnIntentStatus | null;
    returnStatusLoadedProviderId: number | null;

    myRequests: ShipmentRequest[];
    myRequestsMeta: MarketplacePaginationMeta<ShipmentRequestListStatusFilter> | null;
    myRequestsStatusFilter: ShipmentRequestListStatusFilter;
    feedRequests: ShipmentRequest[];
    feedMeta: MarketplacePaginationMeta | null;
    selectedRequest: ShipmentRequest | null;
    requestQuotes: ShipmentQuote[];
    isLoadingRequests: boolean;
    isSavingRequest: boolean;
    isLoadingFeed: boolean;
    isSubmittingQuote: boolean;
    isUpdatingCargoInsurance: boolean;

    myContracts: ShipmentContract[];
    myContractsMeta: MarketplacePaginationMeta<ShipmentContractListStatusFilter> | null;
    myContractsStatusFilter: ShipmentContractListStatusFilter;
    selectedContract: ShipmentContract | null;
    isLoadingContracts: boolean;
    isSavingContract: boolean;
    isSendingInvitation: boolean;
    contractFiles: ShipmentContractFile[];
    isLoadingContractFiles: boolean;
    isUploadingContractFiles: boolean;
    isDeletingContractFile: boolean;
    isDownloadingContractFile: boolean;

    error: string | null;

    fetchMode: () => Promise<void>;
    updateMode: (mode: OperationMode) => Promise<boolean>;

    fetchOperatorProfile: () => Promise<OperatorProfile | null>;
    saveOperatorProfile: (payload: UpsertOperatorProfilePayload) => Promise<boolean>;
    createOperatorBankAccount: (payload: UpsertOperatorBankAccountPayload) => Promise<boolean>;
    updateOperatorBankAccount: (accountId: number, payload: UpsertOperatorBankAccountPayload) => Promise<boolean>;
    deleteOperatorBankAccount: (accountId: number) => Promise<boolean>;
    createOperatorVehicle: (payload: UpsertOperatorVehiclePayload) => Promise<OperatorVehicle | null>;
    updateOperatorVehicle: (vehicleId: number, payload: UpsertOperatorVehiclePayload) => Promise<OperatorVehicle | null>;
    deleteOperatorVehicle: (vehicleId: number) => Promise<boolean>;
    syncOperatorVehiclePhotos: (vehicleId: number, photos: VehiclePhoto[]) => void;
    fetchOperatorTrailers: () => Promise<void>;
    createOperatorTrailer: (payload: UpsertOperatorTrailerPayload) => Promise<boolean>;
    updateOperatorTrailer: (trailerId: number, payload: UpsertOperatorTrailerPayload) => Promise<boolean>;
    deleteOperatorTrailer: (trailerId: number) => Promise<boolean>;
    syncOperatorTrailerPhotos: (trailerId: number, photos: TrailerPhoto[]) => void;
    fetchActiveReturnIntent: () => Promise<OperatorReturnIntent | null>;
    fetchReturnStatus: (providerId?: number | null) => Promise<ReturnIntentStatus | null>;
    activateReturnIntent: (payload: ActivateReturnIntentPayload) => Promise<OperatorReturnIntent | null>;
    cancelReturnIntent: (intentId: number) => Promise<boolean>;
    saveHomeBase: (payload: SaveHomeBasePayload) => Promise<boolean>;
    clearHomeBase: () => Promise<boolean>;

    fetchMyRequests: (options?: {
        page?: number;
        perPage?: number;
        status?: ShipmentRequestListStatusFilter;
        append?: boolean;
    }) => Promise<void>;
    fetchFeedRequests: (options?: number | { page?: number; perPage?: number }) => Promise<void>;
    fetchRequestDetail: (requestId: number) => Promise<ShipmentRequest | null>;
    fetchRequestQuotes: (requestId: number) => Promise<void>;
    createRequest: (payload: CreateShipmentRequestPayload, publishAfterCreate?: boolean) => Promise<ShipmentRequest | null>;
    updateRequest: (requestId: number, payload: CreateShipmentRequestPayload) => Promise<ShipmentRequest | null>;
    updateRequestSchedule: (requestId: number, payload: UpdateShipmentRequestSchedulePayload) => Promise<ShipmentRequest | null>;
    publishRequest: (requestId: number) => Promise<ShipmentRequest | null>;
    cancelRequest: (requestId: number) => Promise<ShipmentRequest | null>;
    duplicateRequest: (requestId: number) => Promise<ShipmentRequest | null>;
    createQuote: (requestId: number, payload: CreateQuotePayload) => Promise<ShipmentQuote | null>;
    selectQuote: (quoteId: number) => Promise<ShipmentContract | null>;
    updateCargoInsurance: (requestId: number, choice: Extract<CargoInsuranceChoice, 'own'>) => Promise<ShipmentRequest>;

    fetchMyContracts: (options?: {
        page?: number;
        perPage?: number;
        status?: ShipmentContractListStatusFilter;
        append?: boolean;
    }) => Promise<void>;
    fetchContractDetail: (contractId: number) => Promise<ShipmentContract | null>;
    assignContractResources: (contractId: number, payload: CreateContractAssignmentPayload) => Promise<ShipmentContract | null>;
    fetchContractFiles: (contractId: number) => Promise<ShipmentContractFile[]>;
    uploadContractFiles: (
        contractId: number,
        files: File[],
        category?: ShipmentContractFileCategory,
        scope?: ShipmentContractFileScope
    ) => Promise<ShipmentContractFile[] | null>;
    downloadContractFile: (
        contractId: number,
        fileId: number,
        defaultFileName?: string
    ) => Promise<FileDownloadResult | null>;
    deleteContractFile: (contractId: number, fileId: number) => Promise<boolean>;
    updateContractPoints: (contractId: number, payload: UpdateContractPointsPayload) => Promise<ShipmentContract | null>;
    cancelContract: (contractId: number, reason: string) => Promise<ShipmentContract | null>;
    sendInvitation: (
        contractId: number,
        payload: CreateContractInvitationPayload
    ) => Promise<{ notificationChannel: 'push' | 'email' | null; invitedUserCreated: boolean } | null>;
    acceptInvitation: (token: string) => Promise<{ contractId: number; chatConversationId: number | null; inviteStatus?: 'accepted'; idempotent?: boolean } | null>;
    declineInvitation: (token: string) => Promise<{ contractId?: number; inviteStatus?: 'declined'; idempotent?: boolean } | null>;
    decideInvitation: (
        invitationId: number,
        decision: 'accept' | 'decline'
    ) => Promise<{ contractId: number; chatConversationId: number | null; inviteStatus: 'accepted' | 'declined'; idempotent: boolean } | null>;

    clearError: () => void;
    markModeOnboarded: () => void;
}

export type MarketplaceSet = {
    (partial: Partial<MarketplaceState> | ((state: MarketplaceState) => Partial<MarketplaceState>)): void;
};

export type MarketplaceGet = () => MarketplaceState;

export function toErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message.trim() !== '') {
        return error.message;
    }

    const apiError = error as MarketplaceApiError | undefined;

    if (apiError?.message && apiError.message.trim() !== '') {
        return apiError.message;
    }

    return fallback;
}

export function normalizeCollection<T>(collection: unknown): T[] {
    if (Array.isArray(collection)) {
        return collection as T[];
    }

    if (
        collection &&
        typeof collection === 'object' &&
        'data' in collection &&
        Array.isArray((collection as { data?: unknown }).data)
    ) {
        return (collection as { data: T[] }).data;
    }

    return [];
}
